import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import jwtAxios from '@projectK/services/auth/JWT';
import PropTypes from 'prop-types';

// Create the context
const RolePermissionsContext = createContext({});

// Provider component
export const RolePermissionsProvider = ({ children }) => {
  const { user } = useAuthUser();
  const [rolePermissions, setRolePermissions] = useState({});
  const [swapRolePermissions, setSwapRolePermissions] = useState({});
  const [swapRoles, setSwapRoles] = useState({});
  const [actualRoleData, setActualRoleData] = useState({});
  const [actualSwapRoles, setActualSwapRoles] = useState({});
  const [roleData, setRoleData] = useState({});
  const [userData, setUserData] = useState(null);

  const fetchRolePermissions = async (roleId, actualRoleId) => {
    if (!user?.customerId || !roleId) return;
    try {
      const userResponse = await jwtAxios.get(`/users/${user.id}`);
      const response = await jwtAxios.get(`/roles/customer/user/${roleId}`);
      const swapResponse = await jwtAxios.get(`/role-module-permissions/swap-role/${roleId}`);
      const actualRoleIdResponse = actualRoleId
        ? await jwtAxios.get(`/roles/customer/user/${actualRoleId}`)
        : [];
      const swapActualRoleIdResponse = actualRoleId
        ? await jwtAxios.get(`/role-module-permissions/swap-role/${actualRoleId}`)
        : [];

      setActualSwapRoles(swapActualRoleIdResponse?.data || {});
      setSwapRoles(swapResponse?.data || {});
      setActualRoleData(actualRoleIdResponse?.data || {});
      setRoleData(response?.data || {});
      setUserData(userResponse?.data || null);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    if (!user?.customerId) return;
    try {
      const responseRoleModulePermissions = await jwtAxios.get(
        `/role-module-permissions/role/permission/list/${user?.roleIds[0]}`
      );
      if(isActualSuperAdmin(user)){ 
        const responseSwapRoleModulePermissions = await jwtAxios.get(
        `/role-module-permissions/role/permission/list/${user?.actualRoleIds?.[0]}`
      );
      setSwapRolePermissions(responseSwapRoleModulePermissions?.data || {});
    }
      setRolePermissions(responseRoleModulePermissions?.data || {});
    } catch (error) {
      console.error(error);
      setRolePermissions({});
    }
  };

  RolePermissionsProvider.propTypes = {
    children: PropTypes.node,
  };

  useEffect(() => {
    fetchRolePermissions(user?.roleIds[0], user?.actualRoleIds?.[0]);
    fetchData();
  }, [user?.customerId, user?.roleIds]);

  // ✅ Memoize the value to avoid creating a new object on every render
  const contextValue = useMemo(
    () => ({
      rolePermissions,
      roleData,
      swapRoles,
      actualRoleData,
      actualSwapRoles,
      userData,
      swapRolePermissions
    }),
    [rolePermissions, roleData, swapRoles, actualRoleData, actualSwapRoles, userData, swapRolePermissions]
  );

  return (
    <RolePermissionsContext.Provider value={contextValue}>
      {children}
    </RolePermissionsContext.Provider>
  );
};

// Hook to access permissions in any component
export const useRolePermissions = () => useContext(RolePermissionsContext);

// Single utility function for both single and multiple actions
export const hasPermission = (rolePermissions, module, actions) => {
  if (!rolePermissions || !module || !actions) return false;

  const modulePermissions = rolePermissions[module] || [];

  if (Array.isArray(actions)) {
    // Multiple actions: true if user has at least one
    return actions.some(action => modulePermissions.includes(action));
  }

  // Single action
  return modulePermissions.includes(actions);
};

// Actual identity checks (who the user really is)
export const isActualSuperAdmin = (user) =>
  Array.isArray(user?.actualRoleMenuFlag) &&
  user.actualRoleMenuFlag.includes('super_admin');

export const isActualParent = (user) =>
  Array.isArray(user?.actualRoleMenuFlag) &&
  user.actualRoleMenuFlag.includes('parent');

export const isActualCoach = (user) =>
  Array.isArray(user?.actualRoleMenuFlag) &&
  user.actualRoleMenuFlag.includes('coach');

  export const isActualAdmin = (user) =>
    Array.isArray(user?.actualRoleMenuFlag) &&
    user.actualRoleMenuFlag.includes('admin');  

// Active profile checks (who they are acting as right now)
export const isParent = (user) =>
  Array.isArray(user?.roleMenuFlag) && user.roleMenuFlag[0]==='parent' || Array.isArray(user?.role_menu_flag) && user.role_menu_flag[0]==='parent';

export const isCoach = (user) =>
  Array.isArray(user?.roleMenuFlag) && user.roleMenuFlag[0]==='coach';

export const isTournamentCoach = (user) =>
  Array.isArray(user?.roleMenuFlag) && user.roleMenuFlag[0]==='tournament_coach';

export const isStudent = (user) =>
  Array.isArray(user?.roleMenuFlag) && user.roleMenuFlag[0]==='student' || Array.isArray(user?.role_menu_flag) && user.role_menu_flag[0]==='student';

export const isSuperAdmin = (user) =>
  Array.isArray(user?.roleMenuFlag) && user.roleMenuFlag.includes('super_admin');

export const isAdmin = (user) =>
  Array.isArray(user?.roleMenuFlag) && user.roleMenuFlag.includes('admin');

export const isParentRoleType = (role) => role?.role_menu_flag === 'parent';

export const isStudentRoleType = (role) => role?.role_menu_flag === 'student';

export const isCoachRoleType = (role) => role?.role_menu_flag === 'coach';

export const isAdminRoleType = (role) => role?.role_menu_flag === 'admin';

export const isParentOrStudentRoleType = (role) =>
  ['parent', 'student'].includes(role?.role_menu_flag);

